import { memo, useMemo } from 'react'
import { CookiesProvider } from 'react-cookie'
import { Auth0Provider } from '@auth0/auth0-react'
import { SentrixProvider } from '@sentrix/react'
import { sanitizeRecord } from '../utils/security'

function Auth0SecurityBoundary({ children }) {
  const auth0Config = useMemo(() => {
    if (typeof window === 'undefined') {
      return null
    }

    const domain = (import.meta.env?.VITE_AUTH0_DOMAIN ?? '').trim()
    const clientId = (import.meta.env?.VITE_AUTH0_CLIENT_ID ?? '').trim()
    if (!domain || !clientId) {
      return null
    }

    const redirectUri = typeof window !== 'undefined' ? window.location.origin : undefined

    return {
      domain,
      clientId,
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
      authorizationParams: sanitizeRecord({
        redirect_uri: redirectUri,
        audience: (import.meta.env?.VITE_AUTH0_AUDIENCE ?? '').trim() || undefined,
        scope: 'openid profile email',
      }),
    }
  }, [])

  if (!auth0Config) {
    return children
  }

  return <Auth0Provider {...auth0Config}>{children}</Auth0Provider>
}

function SentrixSecurityBoundary({ children }) {
  const sentrixConfig = useMemo(
    () => ({
      nonce: import.meta.env?.VITE_CSP_NONCE ?? '',
      policies: sanitizeRecord({
        defaultSrc: ["'self'"],
        scriptSrc: ["'self'", "'unsafe-inline'"],
        styleSrc: ["'self'", "'unsafe-inline'"],
        connectSrc: ["'self'"],
      }),
      reportOnly: Boolean(import.meta.env?.VITE_CSP_REPORT_ONLY === 'true'),
    }),
    [],
  )

  return <SentrixProvider {...sentrixConfig}>{children}</SentrixProvider>
}

function SecurityProviders({ children }) {
  const cookieOptions = useMemo(
    () => ({
      sameSite: 'strict',
      secure: true,
      path: '/',
      maxAge: 60 * 60 * 24 * 180,
    }),
    [],
  )

  return (
    <CookiesProvider defaultSetOptions={cookieOptions}>
      <SentrixSecurityBoundary>
        <Auth0SecurityBoundary>{children}</Auth0SecurityBoundary>
      </SentrixSecurityBoundary>
    </CookiesProvider>
  )
}

export default memo(SecurityProviders)
